import React, { useContext, useReducer, useEffect, useState, useLayoutEffect } from 'react';
import dayjs from 'dayjs';
import {
  AppHeader,
  Button,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Icon,
  useBirthDateValidator,
  Form,
  useFirstNameValidator,
  useLastNameValidator,
  useSelectValidator,
  Radio,
  Typography,
  ButtonGroup,
  CircularProgress,
  Alert,
  AlertTypography,
  Link,
  DateField,
  Container,
  Grid,
  Stack,
} from '@ace-clublabs/shared-component-library';
import { useHistory, useLocation } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { addAssociateFormStatusReducer } from '../application/reducers/addAssociateReducers';
import { generateAddAssociateStatus } from '../application/viewmodels/AddAssociateFormStatus';
import { AssociateInfo } from '../application/models/NewJoinSessionData';
import { convertAssociateFormToAssociateInfo } from '../application/viewmodels/AddAssociateFormStatus';
import { getGenderList } from '../utils/helpers';
import { validateAssociateFormData, validateAssociates } from '../utils/validations';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { CurrentPayPlan, FlowType, RoleType } from '@ace-clublabs/common-membership-api-types';
import { TooltipModalContentProps } from '../components/TooltipModal';
import { UtagType } from '../utils/UtagHelper';
import { calculateFamilyUpgradeCost } from '../utils/helpers';
import { AdditionalMemberTooltip } from '../components/AdditionalMemberTooltip';
import { numberHelper } from '../utils/numberHelper';
import { ClubCode } from '../utils/constants';
import { ClubContentName, getClubContent } from '../utils/getClubContent';

interface LocationState {
  associate?: AssociateInfo;
  isEditing?: boolean;
}

export const AddAssociatePage: React.FC = () => {
  const history = useHistory();

  const { membershipInfo, updateAssociates, refreshSession, loadingInfo, sessionData, isPriceCartLoading, sendTag } =
    useContext(MembershipAppStateContext);

  const genderList = getGenderList(membershipInfo);
  const location = useLocation<LocationState>();
  const associate = sessionData?.formData?.associates?.find(
    (associate) => associate.virtualId === location.state?.associate?.virtualId
  );
  const isEditing = location.state?.isEditing ?? false;
  const [memAddOnTooltipInfo, setMemAddOnTooltipInfo] = useState<TooltipModalContentProps | null>(null);
  const [showActiveMemberWarning, setShowActiveMemberWarning] = useState(false);
  const [isTagging, setIsTagging] = useState(true);
  const [associateForm, dispatchAssociateFormUpdate] = useReducer(
    addAssociateFormStatusReducer,
    generateAddAssociateStatus(associate)
  );

  const submitAssociateOnClick = async () => {
    if (isPriceCartLoading) {
      return;
    }

    const associateInfo = convertAssociateFormToAssociateInfo(associateForm);
    const errors = validateAssociateFormData(associateInfo)['addAssociate'];

    if (!errors) {
      try {
        const modification = associate ? 'update' : 'add';
        await updateAssociates(modification, convertAssociateFormToAssociateInfo(associateForm));
        history.goBack();
      } catch (e) {
        sendTag(UtagType.link, 'associate', {
          error: 'membership:join:associate-current active member (VALD)',
        });
        setShowActiveMemberWarning(true);
      }
    } else {
      dispatchAssociateFormUpdate({ field: 'form', value: null });
    }
  };

  const AS = membershipInfo?.roles.find((role) => role.role === RoleType.ASSOCIATE)!;
  const AD = membershipInfo?.roles.find((role) => role.role === RoleType.ADULT_ASSOCIATE)!;
  const DE = membershipInfo?.roles.find((role) => role.role === RoleType.DEPENDENT)!;

  const isAssociatesOnly = !!AS;
  const { hasReachedMaxAdults, hasReachedMaxDependents, hasReachedMaxAssociates } = validateAssociates(
    membershipInfo!,
    sessionData.formData
  );

  const isAdultDisabled =
    hasReachedMaxAssociates ||
    (associate?.roleType === RoleType.DEPENDENT && hasReachedMaxAdults) ||
    (!associate && hasReachedMaxAdults);

  const isDependentDisabled =
    hasReachedMaxAssociates ||
    (associate?.roleType === RoleType.ADULT_ASSOCIATE && hasReachedMaxDependents) ||
    (!associate && hasReachedMaxDependents);

  const isBirthDateRequired = (roleType: RoleType) => {
    const role = membershipInfo?.roles.find((role) => role.role === roleType)!;
    return role?.isBirthDateRequired || false;
  };

  const familyUpgradeFee = calculateFamilyUpgradeCost(membershipInfo!, sessionData);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo) {
      return;
    }
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:associate' : 'associate';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  }, [membershipInfo]);

  useEffect(() => {
    if (!membershipInfo) {
      return;
    }

    let roleType: RoleType;
    if (associate && associate.roleType) {
      roleType = associate.roleType;
    } else if (isAssociatesOnly) {
      roleType = RoleType.ASSOCIATE;
    } else if (hasReachedMaxAdults) {
      roleType = RoleType.DEPENDENT;
    } else {
      roleType = RoleType.ADULT_ASSOCIATE;
    }

    dispatchAssociateFormUpdate({ field: 'roleType', value: roleType });
  }, [membershipInfo]);

  useEffect(() => {
    const dob = associateForm?.dateOfBirth?.value;
    const firstName = associateForm?.firstName?.value;
    const lastName = associateForm?.lastName?.value;
    const gender = associateForm?.gender?.value;
    if (!dob && !firstName && !lastName && !gender) {
      return;
    } else {
      setTimeout(() => {
        const dateOfBirthInput = document && document.getElementById('dateOfBirth');
        dateOfBirthInput?.focus();
        dateOfBirthInput?.blur();
      }, 0);
    }
  }, [associateForm.roleType.value, associateForm?.gender?.value]);

  const currentRole =
    associateForm.roleType.value === RoleType.ADULT_ASSOCIATE
      ? AD
      : associateForm.roleType.value === RoleType.DEPENDENT
      ? DE
      : AS;

  const firstNameValidator = useFirstNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });

  const lastNameValidator = useLastNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });
  const birthdayValidator = useBirthDateValidator({
    minAge: currentRole?.minimumAge,
    maxAge: currentRole?.maximumAge,
    required: isBirthDateRequired(associateForm?.roleType.value!),
  });

  const genderValidator = useSelectValidator({ validationFeedback: 'immediate' });

  const isEastCentral = membershipInfo?.clubCode === ClubCode.eastcentral;
  const isLessThanMinAge = lessThanMinAge(associateForm?.dateOfBirth?.value!, currentRole?.minimumAge);
  const showEastCentralBirthdayWarning = isEastCentral && isLessThanMinAge && currentRole === DE;

  const dependentHasAgeRange = DE?.maximumAge < 140 && DE?.minimumAge > 0;
  const dependentAgeText = dependentHasAgeRange
    ? `between ${DE?.minimumAge} and ${DE?.maximumAge} yrs`
    : `under ${DE?.maximumAge + 1}yrs`;

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <Container className="add-associate-view" sx={{ paddingLeft: '0 !important', paddingRight: '0 !important' }}>
      <AppHeader sx={{ padding: 0 }} />
      <IconButton
        aria-label="close add associate page"
        className="close-icon"
        disabled={isPriceCartLoading}
        onClick={() => {
          history.goBack();
        }}
        sx={{
          position: 'absolute',
          top: '40px',
          right: '0',
        }}
      >
        <Icon iconName="xmark-regular" color="htmlColor" htmlColor="black" />
      </IconButton>
      <div className="benefit-add-ons">
        <Typography className="contact-details-header" variant="h5Medium">
          Let's add a family member
        </Typography>
        <IconButton
          aria-label="add member info"
          className="add-member-info-icon"
          onClick={() => {
            setMemAddOnTooltipInfo({
              tooltipTitle: 'Add a member',
            });
          }}
        >
          <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
        </IconButton>
      </div>
      {familyUpgradeFee > 0 && sessionData.formData.associates === undefined ? (
        <Typography className="family-upgrade-warning" variant="b1Regular">
          There is a {numberHelper.formatMoney(familyUpgradeFee)} fee for a family upgrade
        </Typography>
      ) : (
        <></>
      )}

      {showActiveMemberWarning ? (
        <div className="add-associate-warning-banner">
          <Alert severity="error" displayCloseButton={false}>
            <AlertTypography>
              We could not add this person to your membership. Please remove them and{' '}
              <Link variant="b2Regular" href="https://www.calif.aaa.com/information/contact-us.html" target="_blank">
                contact membership services
              </Link>
              .
            </AlertTypography>
          </Alert>
        </div>
      ) : (
        <></>
      )}

      {isAssociatesOnly ? (
        <></>
      ) : (
        <div id="role-type-radio">
          <Radio
            id="AD-radio"
            value={AD?.role}
            inputProps={{
              onClick: () => {
                dispatchAssociateFormUpdate({ field: 'roleType', value: RoleType.ADULT_ASSOCIATE });
              },
            }}
            checked={associateForm.roleType.value === RoleType.ADULT_ASSOCIATE}
            disabled={isPriceCartLoading || isAdultDisabled}
            label={
              <>
                <Typography variant="b1Medium">{AD?.typeDescription}</Typography>
                <Typography variant="b1Medium">({AD?.maximumAllowed} allowed)</Typography>
              </>
            }
          />
          <Radio
            id="DE-radio"
            checked={associateForm.roleType.value === RoleType.DEPENDENT}
            inputProps={{
              onClick: () => {
                dispatchAssociateFormUpdate({ field: 'roleType', value: RoleType.DEPENDENT });
              },
            }}
            value={DE?.role}
            disabled={isPriceCartLoading || isDependentDisabled}
            label={
              <>
                <Typography variant="b1Medium">{DE?.typeDescription}</Typography>
                <Typography className="de-max-age" variant="b1Medium">
                  ({dependentAgeText})
                </Typography>
              </>
            }
          />
        </div>
      )}

      <div className="contact-details-form">
        <Form onSubmit={submitAssociateOnClick}>
          <TextField
            value={associateForm.firstName.value}
            name="firstName"
            label="First name"
            id="firstName"
            fullWidth
            onChange={(v) => {
              dispatchAssociateFormUpdate({ field: 'firstName', value: v.currentTarget.value });
            }}
            validator={firstNameValidator}
            sx={{
              '& .MuiFormLabel-asterisk': {
                display: 'none',
              },
            }}
          />
          <TextField
            value={associateForm.lastName.value}
            name="lastName"
            label="Last name"
            id="lastName"
            fullWidth
            onChange={(v) => {
              dispatchAssociateFormUpdate({ field: 'lastName', value: v.currentTarget.value });
            }}
            validator={lastNameValidator}
            sx={{
              '& .MuiFormLabel-asterisk': {
                display: 'none',
              },
            }}
          />
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 1 }}>
              <DateField
                name="dateOfBirth"
                label="Date of birth"
                id="dateOfBirth"
                helperText={isBirthDateRequired(associateForm?.roleType.value!) ? '' : 'Optional'}
                fullWidth
                onClearButtonClick={() => {
                  dispatchAssociateFormUpdate({ field: 'dateOfBirth', value: null });
                }}
                validator={birthdayValidator}
                value={
                  associateForm.dateOfBirth.value === '' || associateForm.dateOfBirth.value === null
                    ? null
                    : dayjs(associateForm.dateOfBirth.value)
                }
                onChange={(v) => {
                  dispatchAssociateFormUpdate({ field: 'dateOfBirth', value: v?.format('MM/DD/YYYY') ?? null });
                }}
                sx={{ marginTop: '0.5rem' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
              <Select
                label="Gender"
                name="gender"
                placeholder="Select one"
                displayEmpty
                id="genderDropdown"
                helperText="Must match state driver’s license or I.D."
                fullWidth={true}
                inputLabelProps={{
                  sx: {
                    marginTop: '0.5rem !important',
                  },
                }}
                value={genderList.find((g) => {
                  return g.genderValue === ((associateForm.gender.value as string) ?? '');
                })}
                getDisplayText={(v: any) => {
                  return v.genderName;
                }}
                validator={genderValidator}
                onChange={(v: any) => {
                  const x = v.target.value;
                  dispatchAssociateFormUpdate({ field: 'gender', value: x.genderValue });
                }}
              >
                {genderList.map((gender, i) => (
                  <MenuItem key={gender.genderName} value={gender as any} id={gender.genderValue}>
                    {gender.genderName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid order={{ xs: 2, md: 3 }}>
              {showEastCentralBirthdayWarning ? (
                <Stack id="east-central-birthday-warning">
                  <Alert className="east-central-birthday-warning" severity="error" displayCloseButton={false}>
                    <AlertTypography>
                      {sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY
                        ? getClubContent(ClubContentName.MDOMinimumAgeWarning, ClubCode.eastcentral)
                        : getClubContent(ClubContentName.ADOMinimumAgeWarning, ClubCode.eastcentral)}
                    </AlertTypography>
                  </Alert>
                </Stack>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>

          <ButtonGroup sx={{ gap: '10px' }}>
            <Button
              label={
                <>
                  {isPriceCartLoading ? (
                    <div className="button-spinner">
                      <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                    </div>
                  ) : (
                    <></>
                  )}
                  {isEditing ? <>Update</> : <> Add Member</>}
                </>
              }
              fullWidth
              variant="contained"
              id="addAssociateButton"
              type="submit"
            ></Button>
            <Button
              label="Cancel"
              fullWidth
              variant="outlined"
              disabled={isPriceCartLoading}
              id="cancelAddAssociateButton"
              onClick={() => {
                history.goBack();
              }}
            ></Button>
          </ButtonGroup>
        </Form>
      </div>
      <AdditionalMemberTooltip
        memAddOnTooltipInfo={memAddOnTooltipInfo}
        setMemAddOnTooltipInfo={setMemAddOnTooltipInfo}
        roles={membershipInfo.roles}
      />
    </Container>
  );
};

function lessThanMinAge(date: string, minAge: number) {
  const dateObj = new Date(date);
  const isValidDate = !isNaN(dateObj.getTime());
  const isLessThanMinAge = dayjs().diff(dayjs(date), 'year') < minAge;
  const isAfterToday = isAfter(date);

  return isValidDate && isLessThanMinAge && !isAfterToday;
}

function isAfter(date: string) {
  const inputDate = new Date(date);
  const currentDate = new Date();

  inputDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  return inputDate > currentDate;
}
